<template>
    <div>
        <div class="mb-6 -mt-4">
            <h1>Investment Experience</h1>
        </div>

        <div class="mb-6">
            <h2>Please tell us your years of experience in the following areas:</h2>
        </div>

        <ValidationObserver ref="investmentExperienceObserver">
            <div class="mb-10">
                <ScaleSliderComponent
                    :data="scaleSliderOptions"
                    name="foreign_exchange"
                    label="Spot &amp; foreign exchange"
                    v-model="investmentExperience.foreign_exchange"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="scaleSliderOptions"
                    name="shares_bonds_funds"
                    label="Shares, bonds &amp; funds"
                    v-model="investmentExperience.shares_bonds_funds"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="scaleSliderOptions"
                    name="stock_index_options"
                    label="Stock &amp; Index Options"
                    v-model="investmentExperience.stock_index_options"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="scaleSliderOptions"
                    name="financial_commodity"
                    label="Financial &amp; Commodity F &amp; O"
                    v-model="investmentExperience.financial_commodity"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mt-20 mb-6">
                <h2>How frequently have you traded in the following products in the past 12 months on an execution-only basis:</h2>
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="tradeFrequencySliderOptions"
                    name="foreign_exchange_trade_frequency"
                    label="<strong>Spot &amp; foreign exchange</strong>"
                    v-model="investmentExperience.foreign_exchange_trade_frequency"
                    @change="updateInvestmentExperience"
                    longLabels
                />
            </div>

             <div class="mb-10">
                <ScaleSliderComponent
                    :data="tradeFrequencySliderOptions"
                    name="shares_bonds_funds_trade_frequency"
                    label="<strong>Shares, bonds &amp; funds</strong>"
                    v-model="investmentExperience.shares_bonds_funds_trade_frequency"
                    @change="updateInvestmentExperience"
                    longLabels
                />
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="tradeFrequencySliderOptions"
                    name="stock_index_options_trade_frequency"
                    label="<strong>Stock &amp; Index Options</strong>"
                    v-model="investmentExperience.stock_index_options_trade_frequency"
                    @change="updateInvestmentExperience"
                    longLabels
                />
            </div>

            <div class="mb-10">
                <ScaleSliderComponent
                    :data="tradeFrequencySliderOptions"
                    name="financial_commodity_trade_frequency"
                    label="<strong>Financial &amp; Commodity F &amp; O</strong>"
                    v-model="investmentExperience.financial_commodity_trade_frequency"
                    @change="updateInvestmentExperience"
                    longLabels
                />
            </div>

            <div class="mt-20 mb-6">
                <p>Are you totally reliant on specific recommendations to trade any of the above?</p>
                <div class="mb-3">
                    <CheckboxComponent
                        name="reliant_on_recommendations_foreign_exchange"
                        label="Spot &amp; foreign exchange"
                        v-model="investmentExperience.reliant_on_recommendations_foreign_exchange"
                        @change="updateInvestmentExperience"
                        rules="optional"
                    />
                </div>
                 <div class="mb-3">
                    <CheckboxComponent
                        name="reliant_on_recommendations_shares_bonds_funds"
                        label="Shares, bonds &amp; funds"
                        v-model="investmentExperience.reliant_on_recommendations_shares_bonds_funds"
                        @change="updateInvestmentExperience"
                        rules="optional"
                    />
                </div>
                 <div class="mb-3">
                    <CheckboxComponent
                        name="reliant_on_recommendations_stock_index_options"
                        label="Stock &amp; Index Options"
                        v-model="investmentExperience.reliant_on_recommendations_stock_index_options"
                        @change="updateInvestmentExperience"
                        rules="optional"
                    />
                </div>
                 <div class="mb-3">
                    <CheckboxComponent
                        name="reliant_on_recommendations_financial_commodity"
                        label="Financial &amp; Commodity F &amp; O"
                        v-model="investmentExperience.reliant_on_recommendations_financial_commodity"
                        @change="updateInvestmentExperience"
                        rules="optional"
                    />
                </div>
            </div>

            <div class="mb-6">
                <h2>Please confirm you have read and agree to the statements below:</h2>
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_risks"
                    name="understand_risks"
                    label="I understand the risks of dealing in futures and options, the gearing involved and the potential for loss of capital in excess of my original investment."
                    v-model="investmentExperience.understand_capital_loss_risks"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_small_movements"
                    name="understand_small_movements"
                    label="I understand that small movements in an underlying security/commodity/instrument can result in my transaction incurring much larger profits or losses, and that the more leverage that is available, the greater the effect will be."
                    v-model="investmentExperience.understand_small_movements_risks"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_potential_risks_markets"
                    name="understand_potential_risks"
                    label="I understand and recognise the potential risks associated in dealing in the markets on which I have experience relating to matters such as settlement, trading, foreign exchange, pricing and costs."
                    v-model="investmentExperience.understand_settlement_risks"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_fees_commissions"
                    name="understand_fees_commissions"
                    label="I understand the effect of fees and commissions charges on trading performance."
                    v-model="investmentExperience.understand_fees_commissions"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_potential_risks_options"
                    name="understand_potential_risks"
                    label="I understand and recognise the potential risks associated with dealing in options relating to option expiry, assignment and exercise."
                    v-model="investmentExperience.understand_options_expiry_risks"
                    @change="updateInvestmentExperience"
                />
            </div>

            <div class="mb-6">
                <CheckboxComponent
                    alternateStyle
                    id="understand_responsibility_of_my_account"
                    name="understand_responsibility_of_my_account"
                    label="I understand that it is my responsibility to monitor my account."
                    v-model="investmentExperience.understand_responsibility_of_my_account"
                    @change="updateInvestmentExperience"
                />
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ValidationObserver } from "vee-validate";
import InputComponent from "@components/inputs/InputComponent";
import DropdownComponent from "@components/inputs/DropdownComponent";
import YesNoSliderComponent from "@components/inputs/YesNoSliderComponent";
import ScaleSliderComponent from "@components/inputs/ScaleSliderComponent";
import stepperValidationService from "@services/stepperValidationService";

export default {
    name: "InvestmentExperienceFormComponent",
    components: {
        ValidationObserver,
        InputComponent,
        DropdownComponent,
        YesNoSliderComponent,
        ScaleSliderComponent,
    },
    props: {
        step: { default: "" },
    },
    data: function () {
        return {
            investmentExperience: {},
            isFormValidated: false,
            isReliantOnRecommendations: false,
            scaleSliderOptions: [
                {
                    value: 0,
                    label: "0",
                },
                {
                    value: 1,
                    label: "1",
                },
                {
                    value: 2,
                    label: "2",
                },
                {
                    value: 3,
                    label: "3",
                },
                {
                    value: 4,
                    label: "4",
                },
                {
                    value: 5,
                    label: "5",
                },
                {
                    value: 6,
                    label: "6",
                },
                {
                    value: 7,
                    label: "7",
                },
                {
                    value: 8,
                    label: "8",
                },
                {
                    value: 9,
                    label: "9",
                },
                {
                    value: 10,
                    label: "10+",
                },
            ],
            tradeFrequencySliderOptions: [
                {
                    value: "10 trades or less per quarter",
                    label: "10 trades or less per quarter",
                },
                {
                    value: "10-50 trades per quarter",
                    label: "10-50 trades per quarter",
                },
                {
                    value: "50+ trades per quarter",
                    label: "50+ trades per quarter",
                }
            ]
        };
    },
    computed: {
        ...mapGetters(["investmentExperienceState"]),
    },
    methods: {
        ...mapActions(["doGetInvestmentExperience", "doUpdateInvestmentExperience"]),
        updateInvestmentExperience() {
            this.doUpdateInvestmentExperience(this.investmentExperience).then((response) => {
                this.investmentExperience = response;
            });
        },
        setIsReliantOnRecommendations(isReliantOnRecommendations = false) {
            if (!isReliantOnRecommendations) {
                this.investmentExperience.reliant_on_recommendations_details = null;
                this.doUpdateInvestmentExperience();
            }
            this.isReliantOnRecommendations = isReliantOnRecommendations;
        },
        doValidateForm() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.investmentExperience,
                this.$refs.investmentExperienceObserver,
                false,
                true
            );
        },
    },
    mounted() {
        this.$root.$on("submittingApplication", () => this.doValidateForm());
        this.$root.$on("stepChanged", (step) => {
            if (this.step < step && !this.isFormValidated) {
                this.doValidateForm();
                this.isFormValidated = true;
            } else if (this.step == step) {
                this.isFormValidated = false;
            }
        });

        this.doGetInvestmentExperience().then(response => this.investmentExperience = response);

        this.isReliantOnRecommendations = !!this.investmentExperience.reliant_on_recommendations_details;

        this.$gtm.trackView('Investment Experience', 'currentPath');
    },
    watch: {
        investmentExperience() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.investmentExperience,
                this.$refs.investmentExperienceObserver
            );
        },
    },
};
</script>
